import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Loader2, Pencil, Plus, Save, Trash2, UserPlus, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Label from '../../components/ui/Label';
import Modal from '../../components/ui/Modal';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import ErrorDialog from '../../components/ui/ErrorDialog';
import RoleSelector from '../../components/RoleSelector';
import OrganizationPropertiesEditor from '../../components/OrganizationPropertiesEditor';
import { useAuth } from '../../contexts/AuthContext';
import { catalogForOrganization } from '../../services/roleCatalog';
import { getRoleCatalog, orgMembersService, roleLabel } from '../../services/rolesService';
import { FASTCUBE_ORG, organizationsService } from '../../services/organizationsService';
import { adminOrgsApi, apiErrorText, ORG_NAME_RULE, parsePublisherIds, sanitizeOrgName } from './adminOrgsApi';

const DEFAULT_MEMBER_ROLES = ['operator'];

/**
 * Platform-admin organization detail: name/description, publisher ids, custom universe
 * properties and members with their roles.
 */
function AdminOrgDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: me } = useAuth();

  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [confirm, setConfirm] = useState(null);

  const [details, setDetails] = useState({ name: '', description: '' });
  const [savingDetails, setSavingDetails] = useState(false);

  const [publishers, setPublishers] = useState([]);
  const [newPublisher, setNewPublisher] = useState('');
  const [savingPublishers, setSavingPublishers] = useState(false);

  const [catalog, setCatalog] = useState(null);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ email: '', first_name: '', last_name: '', roles: DEFAULT_MEMBER_ROLES });
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editRoles, setEditRoles] = useState([]);
  const [savingRoles, setSavingRoles] = useState(false);

  const isFastCube = org?.name === FASTCUBE_ORG;

  const loadMembers = useCallback(async () => {
    setMembersLoading(true);
    try {
      setMembers(await orgMembersService.list(id));
    } catch (err) {
      setMembers([]);
      setErrorMessage(apiErrorText(err, 'Failed to load members.'));
    } finally {
      setMembersLoading(false);
    }
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [rows, cat] = await Promise.all([adminOrgsApi.list(), getRoleCatalog().catch(() => null)]);
        if (cancelled) return;
        setCatalog(cat);
        const found = (rows || []).find((o) => String(o.id) === String(id));
        if (!found) {
          setError('Organization not found.');
          return;
        }
        setOrg(found);
        setDetails({ name: found.name || '', description: found.description || '' });
        setPublishers(parsePublisherIds(found.publisher_ids));
      } catch (err) {
        if (!cancelled) setError(apiErrorText(err, 'Failed to load organization.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    loadMembers();
    return () => {
      cancelled = true;
    };
  }, [id, loadMembers]);

  const applyUpdate = async (payload, okText, setBusy) => {
    setBusy(true);
    try {
      const updated = await adminOrgsApi.update(org.id, payload);
      setOrg(updated);
      setPublishers(parsePublisherIds(updated.publisher_ids));
      organizationsService.clearOrganizations();
      toast.success(okText);
      return true;
    } catch (err) {
      setErrorMessage(apiErrorText(err, 'Failed to update organization.'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const saveDetails = async () => {
    const name = details.name.trim();
    if (!name) {
      toast.error('Name is required.');
      return;
    }
    const payload = { description: details.description.trim() || null };
    if (name !== org.name) payload.name = name;
    await applyUpdate(payload, 'Organization saved', setSavingDetails);
  };

  const savePublishers = (next, okText) => applyUpdate({ publisher_ids: next.join(',') }, okText, setSavingPublishers);

  const addPublisher = async () => {
    const incoming = [...new Set(newPublisher.split(/[\s,]+/).map((id) => id.trim()).filter(Boolean))];
    if (incoming.length === 0) return;
    const existing = new Set(publishers);
    const toAdd = incoming.filter((id) => !existing.has(id));
    if (toAdd.length === 0) {
      toast.error(
        incoming.length === 1
          ? 'That publisher ID is already on this organization.'
          : 'Those publisher IDs are already on this organization.'
      );
      return;
    }
    const skipped = incoming.length - toAdd.length;
    const addedText = toAdd.length === 1 ? 'Publisher added' : `${toAdd.length} publishers added`;
    const okText =
      skipped === 0
        ? addedText
        : `${addedText}. ${skipped} ${skipped === 1 ? 'was' : 'were'} already on this organization.`;
    if (await savePublishers([...publishers, ...toAdd], okText)) setNewPublisher('');
  };

  const removePublisher = (value) => savePublishers(publishers.filter((p) => p !== value), 'Publisher removed');

  const deactivate = () =>
    setConfirm({
      title: 'Deactivate organization',
      message: `Deactivate ${org.name}? This is blocked while it still has members.`,
      confirmText: 'Deactivate',
      run: async () => {
        try {
          await adminOrgsApi.deactivate(org.id);
          organizationsService.clearOrganizations();
          toast.success(`${org.name} deactivated`);
          navigate('/admin/organizations', { replace: true });
        } catch (err) {
          setErrorMessage(apiErrorText(err, 'Failed to deactivate organization.'));
        }
      },
    });

  const submitAdd = async () => {
    const email = addForm.email.trim();
    if (!email) {
      toast.error('Enter an email address.');
      return;
    }
    setAdding(true);
    try {
      const created = await orgMembersService.add(org.id, {
        email,
        first_name: addForm.first_name.trim(),
        last_name: addForm.last_name.trim(),
        roles: addForm.roles,
      });
      setMembers((prev) => [...prev.filter((m) => m.user_id !== created.user_id), created]);
      setAddOpen(false);
      setAddForm({ email: '', first_name: '', last_name: '', roles: DEFAULT_MEMBER_ROLES });
      organizationsService.clearOrganizationsListCookie();
      toast.success(`${created.email} added to ${org.name}`);
    } catch (err) {
      toast.error(apiErrorText(err, 'Failed to add member.'));
    } finally {
      setAdding(false);
    }
  };

  const submitRoles = async () => {
    if (!editing) return;
    setSavingRoles(true);
    try {
      const updated = await orgMembersService.setRoles(org.id, editing.user_id, editRoles);
      setMembers((prev) => prev.map((m) => (m.user_id === updated.user_id ? updated : m)));
      setEditing(null);
      toast.success('Roles updated');
    } catch (err) {
      toast.error(apiErrorText(err, 'Failed to update roles.'));
    } finally {
      setSavingRoles(false);
    }
  };

  const removeMember = (m) =>
    setConfirm({
      title: 'Remove member',
      message: `Remove ${m.email} from ${org.name}? Their account and other organizations are not affected.`,
      confirmText: 'Remove',
      run: async () => {
        try {
          await orgMembersService.remove(org.id, m.user_id);
          setMembers((prev) => prev.filter((x) => x.user_id !== m.user_id));
          organizationsService.clearOrganizationsListCookie();
          toast.success(`${m.email} removed`);
        } catch (err) {
          setErrorMessage(apiErrorText(err, 'Failed to remove member.'));
        }
      },
    });

  const sortedMembers = [...members].sort((a, b) =>
    (a.first_name || '').localeCompare(b.first_name || '') || (a.email || '').localeCompare(b.email || '')
  );

  return (
    <div className="space-y-6 pb-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to="/admin/organizations" className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-[#18181B]">
            <ArrowLeft className="h-4 w-4" />
            Organizations
          </Link>
          <h2 className="text-lg font-semibold text-[#18181B]">{org?.name || 'Organization'}</h2>
          {isFastCube ? <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-800">Platform</span> : null}
        </div>
        {org && !isFastCube ? (
          <Button variant="ghost" onClick={deactivate} className="gap-1.5 border border-hairline text-[#F53D7A]">
            <Trash2 className="h-4 w-4" />
            Deactivate
          </Button>
        ) : null}
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="org-name">Name</Label>
                  <Input id="org-name" value={details.name} onChange={(e) => setDetails((d) => ({ ...d, name: sanitizeOrgName(e.target.value) }))} disabled={savingDetails} />
                  <p className="text-xs text-zinc-500">{ORG_NAME_RULE} Renaming also renames the universe tables in Databricks.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="org-desc">Description</Label>
                  <Input id="org-desc" value={details.description} onChange={(e) => setDetails((d) => ({ ...d, description: e.target.value }))} placeholder="Optional" disabled={savingDetails} />
                </div>
              </div>
              <div className="flex justify-start">
                <Button variant="outline" size="icon" onClick={saveDetails} disabled={savingDetails} aria-label="Save details" title="Save details">
                  {savingDetails ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Publishers</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {isFastCube ? (
                <p className="text-sm text-zinc-500">The platform organization sees every publisher; no restriction applies.</p>
              ) : (
                <>
                  <p className="text-xs text-zinc-500">Offer reports are limited to these publisher IDs. With none, the organization sees no offers.</p>
                  <div className="flex flex-wrap gap-2">
                    {publishers.length === 0 ? <span className="text-sm text-amber-700">No publishers yet.</span> : null}
                    {publishers.map((p) => (
                      <span key={p} className="inline-flex items-center gap-1 rounded-full bg-[#E8EEF5] px-2.5 py-1 text-sm text-[#18181B]">
                        {p}
                        <button type="button" onClick={() => removePublisher(p)} disabled={savingPublishers} className="rounded-full p-0.5 text-zinc-500 hover:bg-white hover:text-[#F53D7A]" aria-label={`Remove publisher ${p}`}>
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex max-w-xl items-center gap-2">
                    <Input
                      value={newPublisher}
                      onChange={(e) => setNewPublisher(e.target.value.replace(/[^0-9,\s]/g, ''))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') addPublisher();
                      }}
                      placeholder="Publisher IDs, separated by spaces"
                      disabled={savingPublishers}
                      aria-label="New publisher IDs"
                    />
                    <Button variant="outline" size="icon" onClick={addPublisher} disabled={savingPublishers || !newPublisher.trim()} aria-label="Add publishers" title="Add publishers">
                      {savingPublishers ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <Card className="overflow-visible">
            <CardHeader>
              <CardTitle>Custom properties</CardTitle>
            </CardHeader>
            <CardContent className="overflow-visible">
              <OrganizationPropertiesEditor
                organizationId={org.id}
                apiBase="/api/v2/admin/organizations"
                disabled={isFastCube}
                disabledReason="The platform organization has no universe table, so custom properties are not available."
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <CardTitle>
                  Members
                  {!membersLoading ? <span className="ml-2 text-sm font-normal text-zinc-500">({members.length})</span> : null}
                </CardTitle>
                <Button variant="outline" size="icon" onClick={() => setAddOpen(true)} aria-label="Add member" title="Add member">
                  <UserPlus className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {membersLoading ? (
                <p className="text-sm text-gray-500">Loading members…</p>
              ) : sortedMembers.length === 0 ? (
                <p className="text-sm text-gray-500">No members yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-hairline text-left text-xs uppercase tracking-wide text-zinc-500">
                        <th className="py-2 pr-3 font-medium">Member</th>
                        <th className="py-2 pr-3 font-medium">Roles in {org.name}</th>
                        <th className="py-2 text-right font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedMembers.map((m) => {
                        const isSelf = String(m.user_id) === String(me?.id);
                        return (
                          <tr key={m.user_id} className={`border-b border-gray-100 ${m.is_active ? '' : 'opacity-60'}`}>
                            <td className="py-2 pr-3 align-top">
                              <Link to={`/admin/users/${m.user_id}`} className="font-medium text-[#18181B] hover:text-[#0062FB] hover:underline">
                                {[m.first_name, m.last_name].filter(Boolean).join(' ') || m.email}
                              </Link>
                              <div className="text-xs text-zinc-500">{m.email}</div>
                            </td>
                            <td className="py-2 pr-3 align-top">
                              <div className="flex flex-wrap gap-1">
                                {m.is_platform_admin ? <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-800">Platform Admin</span> : null}
                                {(m.roles || []).length === 0 && !m.is_platform_admin ? <span className="text-xs text-amber-700">No roles</span> : null}
                                {(m.roles || []).map((r) => (
                                  <span key={r} className="rounded-full bg-[#E8EEF5] px-2 py-0.5 text-xs text-[#18181B]">
                                    {roleLabel(catalog, r)}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td className="py-2 align-top text-right">
                              <div className="inline-flex items-center gap-0.5">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditing(m);
                                    setEditRoles(m.roles || []);
                                  }}
                                  className="rounded p-1.5 text-[#0062FB] hover:bg-blue-50"
                                  title="Edit roles"
                                  aria-label="Edit roles"
                                >
                                  <Pencil className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => removeMember(m)}
                                  disabled={isSelf}
                                  className="rounded p-1.5 text-[#F53D7A] hover:bg-pink-50 disabled:opacity-40"
                                  title={isSelf ? 'You cannot remove yourself' : 'Remove from organization'}
                                  aria-label="Remove from organization"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <Modal isOpen={addOpen} onClose={() => !adding && setAddOpen(false)} className="w-full max-w-2xl mx-4">
        <CardContent className="space-y-4 pt-6">
          <h2 className="pr-8 text-lg font-semibold text-[#18181B]">Add a member to {org?.name}</h2>
          <p className="text-sm text-zinc-500">An existing account is linked to this organization. A new email creates the account and sends an invite with a temporary password.</p>
          <div className="space-y-2">
            <Label htmlFor="m-email">Email</Label>
            <Input id="m-email" type="email" value={addForm.email} onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))} placeholder="person@company.com" />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="m-first">First name</Label>
              <Input id="m-first" value={addForm.first_name} onChange={(e) => setAddForm((f) => ({ ...f, first_name: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="m-last">Last name</Label>
              <Input id="m-last" value={addForm.last_name} onChange={(e) => setAddForm((f) => ({ ...f, last_name: e.target.value }))} />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Roles</Label>
            <RoleSelector catalog={catalogForOrganization(catalog, org?.name)} value={addForm.roles} onChange={(roles) => setAddForm((f) => ({ ...f, roles }))} idPrefix="org-add-role" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setAddOpen(false)} disabled={adding}>
              Cancel
            </Button>
            <Button variant="outline" onClick={submitAdd} disabled={adding} className="gap-1.5">
              {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
              Add member
            </Button>
          </div>
        </CardContent>
      </Modal>

      <Modal isOpen={Boolean(editing)} onClose={() => !savingRoles && setEditing(null)} className="w-full max-w-2xl mx-4">
        {editing ? (
          <CardContent className="space-y-4 pt-6">
            <h2 className="pr-8 text-lg font-semibold text-[#18181B]">
              Roles for {[editing.first_name, editing.last_name].filter(Boolean).join(' ') || editing.email}
            </h2>
            <RoleSelector catalog={catalogForOrganization(catalog, org?.name)} value={editRoles} onChange={setEditRoles} idPrefix="org-edit-role" />
            {editRoles.length === 0 ? <p className="text-sm text-amber-700">With no roles this person will not see anything in {org?.name}.</p> : null}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setEditing(null)} disabled={savingRoles}>
                Cancel
              </Button>
              <Button variant="outline" onClick={submitRoles} disabled={savingRoles} className="gap-1.5">
                {savingRoles ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Save roles
              </Button>
            </div>
          </CardContent>
        ) : null}
      </Modal>

      <ConfirmDialog
        isOpen={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        onConfirm={() => confirm?.run()}
        title={confirm?.title || 'Confirm'}
        message={confirm?.message || ''}
        confirmText={confirm?.confirmText || 'OK'}
        cancelText="Cancel"
      />
      <ErrorDialog isOpen={Boolean(errorMessage)} onClose={() => setErrorMessage('')} title="Error" message={errorMessage} />
    </div>
  );
}

export default AdminOrgDetail;
